(() => {
  const config = window.wholesaleSettings || { enabled: false, maxQuantity: 25 };
  const cap = () => parseInt(config.maxQuantity, 10) || 25;
  const cartUrl = () => window.routes?.cart_url || '/cart';
  const addUrl = () => window.routes?.cart_add_url || '/cart/add.js';
  const updateUrl = () => window.routes?.cart_update_url || '/cart/update.js';

  let cartPromise = null;
  let cartFetchedAt = 0;
  async function getCart(force = false) {
    if (!force && cartPromise && Date.now() - cartFetchedAt < 500) return cartPromise;
    cartFetchedAt = Date.now();
    cartPromise = fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error('Unable to read cart');
      return response.json();
    });
    return cartPromise;
  }

  function variantQuantity(cart, variantId) {
    const id = parseInt(variantId, 10);
    return cart.items.filter((item) => item.variant_id === id).reduce((total, item) => total + item.quantity, 0);
  }

  function limitMessage(current = 0) {
    return (config.limitMessage || 'You can order a maximum of [max] units of the same variant.')
      .replace('[max]', cap()).replace('[current]', current);
  }

  async function validateAdd(variantId, addQuantity) {
    if (!config.enabled) return { ok: true };
    const cart = await getCart(true);
    const current = variantQuantity(cart, variantId);
    const ok = current + addQuantity <= cap();
    return { ok, current, remaining: Math.max(0, cap() - current), message: ok ? '' : limitMessage(current) };
  }

  async function validateCart(cart = null) {
    if (!config.enabled) return { ok: true, violations: [] };
    cart = cart || await getCart(true);
    const totals = new Map();
    cart.items.forEach((item) => totals.set(item.variant_id, (totals.get(item.variant_id) || 0) + item.quantity));
    const violations = [...totals.entries()].filter(([, qty]) => qty > cap());
    return { ok: violations.length === 0, violations };
  }

  function renderSections(state) {
    if (!state?.sections) return;
    const mappings = [
      ['cart-icon-bubble', '#shopify-section-cart-icon-bubble'],
      ['cart-drawer', '#CartDrawer .drawer__inner'],
      ['cart-notification-product', '#cart-notification-product']
    ];
    for (const [section, selector] of mappings) {
      if (!state.sections[section]) continue;
      const parsed = new DOMParser().parseFromString(state.sections[section], 'text/html');
      const source = parsed.querySelector(selector) || parsed.body.firstElementChild;
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    }
  }

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart update failed');
    return data;
  }

  async function setVariantTotals(targets) {
    const normalized = Object.fromEntries(Object.entries(targets).map(([id, qty]) => [String(parseInt(id, 10)), Math.max(0, Math.min(cap(), parseInt(qty, 10) || 0))]));
    let cart = await getCart(true);
    const reductions = {};
    const additions = [];

    for (const [variantId, target] of Object.entries(normalized)) {
      const matching = cart.items.filter((item) => item.variant_id === parseInt(variantId, 10));
      const current = matching.reduce((sum, item) => sum + item.quantity, 0);
      if (target < current) {
        let remaining = target;
        matching.forEach((item) => {
          const keep = Math.min(item.quantity, remaining);
          reductions[item.key] = keep;
          remaining -= keep;
        });
      } else if (target > current) {
        additions.push({ id: parseInt(variantId, 10), quantity: target - current });
      }
    }

    let state = null;
    if (Object.keys(reductions).length) {
      const reductionBody = { updates: reductions };
      if (!additions.length) {
        reductionBody.sections = ['cart-icon-bubble', 'cart-drawer'];
        reductionBody.sections_url = window.location.pathname;
      }
      state = await postJson(updateUrl(), reductionBody);
    }
    if (additions.length) {
      state = await postJson(addUrl(), {
        items: additions,
        sections: ['cart-icon-bubble', 'cart-drawer', 'cart-notification-product'],
        sections_url: window.location.pathname
      });
    }
    renderSections(state);
    cart = await getCart(true);
    if (window.PUB_SUB_EVENTS && window.publish) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'wholesale-order', cartData: cart });
    return cart;
  }

  window.WholesaleQuantity = { enabled: !!config.enabled, getCart, variantQuantity, validateAdd, validateCart, setVariantTotals };

  class WholesaleOrderRow extends HTMLElement {
    connectedCallback() {
      this.variantControl = this.querySelector('[data-wholesale-variant]');
      this.quantityInput = this.querySelector('[data-wholesale-quantity]');
      this.status = this.querySelector('[data-wholesale-row-status]');
      this.autoUpdate = this.dataset.autoUpdate === 'true';
      this.updateTimer = null;

      this.querySelector('[data-wholesale-minus]')?.addEventListener('click', () => this.change(-1));
      this.querySelector('[data-wholesale-plus]')?.addEventListener('click', () => this.change(1));
      this.querySelector('[data-wholesale-plus-five]')?.addEventListener('click', () => this.change(5));

      this.quantityInput?.addEventListener('input', () => {
        this.quantityInput.value = Math.min(cap(), Math.max(0, parseInt(this.quantityInput.value || '0', 10) || 0));
      });
      this.quantityInput?.addEventListener('change', () => this.scheduleCommit());

      if (this.quantityInput) this.sync();
    }

    variantId() {
      return parseInt(this.dataset.variantId || this.variantControl?.value || '0', 10);
    }

    change(delta) {
      if (!this.quantityInput) return;
      this.quantityInput.value = Math.min(
        cap(),
        Math.max(0, (parseInt(this.quantityInput.value || '0', 10) || 0) + delta)
      );
      this.scheduleCommit();
    }

    scheduleCommit() {
      if (!this.autoUpdate || !this.quantityInput) return;
      window.clearTimeout(this.updateTimer);
      this.updateTimer = window.setTimeout(() => this.commit(), 300);
    }

    async sync(cart = null) {
      if (!this.quantityInput) return;
      try {
        cart = cart || await getCart();
        this.quantityInput.value = variantQuantity(cart, this.variantId());
      } catch (e) {
        console.error(e);
      }
    }

    target() {
      if (!this.quantityInput) return null;
      return [this.variantId(), parseInt(this.quantityInput.value || '0', 10) || 0];
    }

    async commit() {
      const target = this.target();
      if (!target) return;

      const [id, qty] = target;
      if (this.status) this.status.textContent = 'Updating…';
      this.classList.add('is-updating');

      try {
        const cart = await setVariantTotals({ [id]: qty });
        await this.sync(cart);
        if (this.status) {
          this.status.textContent = qty > 0 ? `Added to cart · ${qty}/${cap()}` : 'Removed from cart';
          this.status.classList.add('is-visible');
          window.clearTimeout(this.statusTimer);
          this.statusTimer = window.setTimeout(() => this.status.classList.remove('is-visible'), 1800);
        }
      } catch (e) {
        if (this.status) {
          this.status.textContent = e.message;
          this.status.classList.add('is-visible');
        }
      } finally {
        this.classList.remove('is-updating');
      }
    }
  }
  if (!customElements.get('wholesale-order-row')) customElements.define('wholesale-order-row', WholesaleOrderRow);

  class WholesaleQuickOrder extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-wholesale-submit-all]');
      this.status = this.querySelector('[data-wholesale-order-status]');
      this.button?.addEventListener('click', () => this.submitAll());
    }
    async submitAll() {
      const rows = [...this.querySelectorAll('wholesale-order-row')];
      const targets = {};
      rows.forEach((row) => {
        const target = row.target();
        if (!target) return;
        const [id, qty] = target;
        targets[id] = qty;
      });
      this.button.disabled = true; this.status.textContent = 'Updating cart…';
      try {
        const cart = await setVariantTotals(targets);
        rows.forEach((row) => row.sync(cart));
        this.status.textContent = 'Cart updated.';
      } catch (e) { this.status.textContent = e.message; }
      finally { this.button.disabled = false; }
    }
  }
  if (!customElements.get('wholesale-quick-order')) customElements.define('wholesale-quick-order', WholesaleQuickOrder);

  // Final theme-level checkout guard. Accelerated checkout is hidden by Liquid when enabled.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[name="checkout"]');
    if (!button || !config.enabled || button.dataset.wholesaleValidated === 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const original = button.textContent;
    button.disabled = true; button.textContent = 'Checking quantities…';
    try {
      const validation = await validateCart();
      if (!validation.ok) {
        const message = (config.checkoutMessage || 'Please reduce each variant to [max] units or fewer before checkout.').replace('[max]', cap());
        alert(message);
        return;
      }
      button.dataset.wholesaleValidated = 'true';
      button.disabled = false; button.textContent = original;
      button.click();
    } catch (e) { alert(e.message); }
    finally {
      if (button.dataset.wholesaleValidated !== 'true') { button.disabled = false; button.textContent = original; }
    }
  }, true);
})();
